import React, { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

import { blankStyleOverrides, stylingOverrideReturns } from "../../utils";
import { ParamsProps } from "../../live-event";
import { LanguagesAbbr, Session, SessionPreview, SessionTypesEnum, } from "../../../../types/working-model";
import { Tag } from "../../../general-ui/tag/tag";
import { EventsState } from "../../../../store/types";
import { Button } from "../../../general-ui/button/button";
import { TypographyItem } from "../../../general-ui/typography-item/typography-item";
import { displayAddToCalendarButton, isSafari } from "../../../../utils/utils";
import Icon, { ICONS } from "../../../general-ui/icon";
import { hasEventAccess, hasSessionAccess } from "../../../../utils/registration-utils";
import { useTypedSelector } from "../../../../store/reducers/use-typed-selector";
import { updateMyListSessions } from "../../../../store/actions/event/event-actions";
import { useTranslate } from "../../../../i18n/useTranslationModules";
import SessionTags from "./session-tags";
import RenderEditorDescription from "../../../general-ui/editable-text/render-editor-description";
import { OptionalComponent } from "../../../../utils/optional-component";
import handleSessionCardClick from "../../../../utils/handle-session-card-click";
import useGetSessionTags from "../../../../utils/use-get-session-tags";
import { useGetTagText } from "../../../../hooks/agenda.hooks";
import { useGetGoToSessionButtonText } from "../../../../hooks/button-text.hooks";
import { EIcon } from "../../../general-ui/icon/icon";
import NavigationDropdown from "../../../general-ui/dropdown/navigation-dropdown";
import { useScreenMediaQuery } from "../../../../utils/use-screen-media-query";
const AddToCalendarModal = lazy(() => import("./add-to-calendar-modal/add-to-calendar-modal"));

interface SessionCardProps {
	session: SessionPreview | Session;
	stylingOverrides?: stylingOverrideReturns;
	template: string;
	defaultImage: string;
	selectedLanguage?: LanguagesAbbr;
	editorSize?: 'desktop' | 'tablet' | 'mobile';
	isLandingPage?: boolean;
	isCalendarLayout?: boolean;
	featuredCard?: boolean;
}

const SessionCard: React.FC<SessionCardProps> = ({
	session,
	stylingOverrides = blankStyleOverrides,
	template,
	defaultImage,
	selectedLanguage,
	isCalendarLayout = false,
	featuredCard = false,
}) => {
	const {
		validSessions,
		finishedRegistering,
		userVerified,
		paidSessions,
		liveSessions,
		firesidesHostSessions,
		userSavedSessions
	} = useSelector((event: EventsState) => event.LiveEventReducer);
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const token = useTypedSelector(state => state.LiveEventReducer.blProfileUserToken);

	const { t } = useTranslate(['landing', 'homepage']);
	const dispatch = useDispatch();
	const { isLessThan768, isLessThan1024 } = useScreenMediaQuery();

	const [openAddToCalendar, setOpenAddToCalendar] = useState(false);
	const [actionMenuOpen, setActionMenuOpen] = useState(false);

	const { language, eventName, uuid }: ParamsProps = useParams();
	const history = useHistory();
	const event = eventBundle ?? workingEvent;
	const isOnDemand = session.session_type === 'on-demand';
	const isBroadcast = session.session_type === SessionTypesEnum.broadcast;
	const isBreakoutRooms = session.session_type === SessionTypesEnum.breakoutRooms;
	const isFireside = session.session_type === SessionTypesEnum.fireside;
	const sessionTags = useGetSessionTags(event, session);

	const showAddToCalBtn = event?.settings?.show_add_to_cal_btn;
	const showAddToCalendarButton = useMemo(() => {
		if (!session) return false;
		return displayAddToCalendarButton(session, showAddToCalBtn);
	}, [session, showAddToCalBtn]);


	const {
		moduleDescriptionOverrides,
		moduleCustomHeadingOverrides,
		moduleTitleOverrides,
		secondaryButtonOverrides
	} = stylingOverrides;

	const sessionAccess = hasSessionAccess(event, session, validSessions, paidSessions);

	const myListActive = event?.settings.personalizedAttendeeList;

	const closeAddToCalendar = useCallback(() => {
		setOpenAddToCalendar(false);
	}, []);

	const openAddToCalendarModal = (e: React.SyntheticEvent) => {
		e.stopPropagation();
		setOpenAddToCalendar(true);
	};

	const userHasAccess = !!workingEvent || hasEventAccess(event, validSessions, finishedRegistering, userVerified, firesidesHostSessions);

	const handleSessionClick = () => {
		handleSessionCardClick({
			event,
			workingEvent,
			session,
			language,
			eventName,
			validSessions,
			finishedRegistering,
			userVerified,
			firesidesHostSessions,
			paidSessions,
			paramsUuid: uuid,
			history,
			selectedLanguage,
		});
	};

	const handleMyListClick = () => {
		if (token && event?.uuid) {
			if (userSavedSessions?.length && userSavedSessions?.includes(session.uuid)) {
				const newList = userSavedSessions.filter(uuid => uuid !== session.uuid);
				dispatch(updateMyListSessions(token, event.uuid, newList));
			} else {
				dispatch(updateMyListSessions(token, event.uuid, [...userSavedSessions ?? [], session.uuid]));
			}
		}
	};

	const isBroadcastLive = useMemo(() => {
		// Broadcast sessions are live if the broadcast is actually live on the stream, not the date
		return liveSessions.includes(session.uuid);
	}, [session, liveSessions]);

	const isBreakoutRoomsLive = (session: Session | SessionPreview) => {
		if (!session.timestamp || !session.end_timestamp) return;
		const date = Date.now();
		const timestamp = new Date(session.timestamp);
		const end_timestamp = new Date(session.end_timestamp);
		if (timestamp.getTime() < date && end_timestamp.getTime() > date) {
			return true;
		} else {
			return false;
		}
	};

	const isSessionLive = (session: Session | SessionPreview) => {
		if (isBroadcast) {
			return isBroadcastLive;
		} else if (isBreakoutRooms || isFireside) {
			return isBreakoutRoomsLive(session);
		}
	};

	const sessionInMyList = useMemo(() => {
		return userSavedSessions && userSavedSessions?.includes(session.uuid);
	}, [userSavedSessions, session.uuid]);

	const myListButtonText = sessionInMyList ? t('homepage:Remove from my list', "") : t('homepage:Add to my list', "");

	const sessionCardDescription = t(`agenda.session.${session.session}.description`, session.description?.[language] as string || session.description?.base || '');

	const disableSessionThumbnails = event?.settings?.disable_session_thumbnails;

	const shouldShowTimestamp = (isBroadcast || isBreakoutRooms);

	const timestampText = useGetTagText({
		session_type: session.session_type,
		shouldShowTimestamp,
		timestamp: session.timestamp,
		language,
		tracks: session.tracks,
	});

	const buttonText = useGetGoToSessionButtonText(session, !!isSessionLive(session));

	const renderStandardSessionCard = () => (

		<div className={classNames("agenda-session-card", {
			'no-image': disableSessionThumbnails,
			'featured-card': featuredCard,
			'live': !isOnDemand && isSessionLive(session),
		})}>
			<div
				className={
					classNames(
						"agenda-session-card-image",
						moduleCustomHeadingOverrides,
						{ 'no-image': disableSessionThumbnails }
					)
				}
				onClick={handleSessionClick}
			>
				<OptionalComponent display={!disableSessionThumbnails}>
					{session.image ? <img src={session.image} alt={"session image"} /> : <img src={defaultImage} alt={"session image"} />}
				</OptionalComponent>
				{!isOnDemand && isSessionLive(session) && (
					<div className="agenda-session-card-tags-wrapper">
						<Tag
							classNameTag={classNames("tag-container-live", moduleCustomHeadingOverrides)}
							text={t("homepage:live", "")}
						/>
					</div>
				)}
			</div>
			<div className="agenda-session-card-content">
				<div className="agenda-session-card-text-content">
					<TypographyItem
						className={classNames('agenda-session-card-timestamp evt-typography-caption-1 text-ellipsis', moduleCustomHeadingOverrides)}
						onClick={handleSessionClick}
					>
						{timestampText}
					</TypographyItem>
					<TypographyItem
						className={classNames('agenda-session-card-title evt-typography-h3 text-ellipsis', moduleTitleOverrides)}
						tagName="h4"
						onClick={handleSessionClick}
					>
						{t(`agenda.session.${session.session}.title`, session.title[language] as string || session.title.base || '')}
					</TypographyItem>
					{session.description &&
						<TypographyItem className={classNames('agenda-session-card-description evt-typography-subtitle-b2', moduleDescriptionOverrides)}>
							<RenderEditorDescription
								description={sessionCardDescription}
								useNew
								stopPropagation // to prevent anchor tags from triggering the handleSessionClick that lives on the parent div above
							/>
						</TypographyItem>
					}
				</div>
				<div className="agenda-session-card-buttons">
					<Button
						classButton={classNames(
							"primary",
							"max-width-100-percent",
							secondaryButtonOverrides)}
						template={template}
						onClick={handleSessionClick}
						isDisabled={paidSessions && !sessionAccess}
					>
						{buttonText}
					</Button>

					{/* don't show My List button on pre-registration pages */}
					{myListActive && userHasAccess && (
						<Button
							classButton={classNames(
								"secondary with-svg",
								secondaryButtonOverrides)}
							template={template}
							onClick={handleMyListClick}
							isDisabled={false}
							title={myListButtonText}
						>
							{sessionInMyList ?
								<Icon name={ICONS.DONE} color="" size={22} /> :
								<Icon name={ICONS.ADD} color="" size={22} />
							}
						</Button>
					)}

					{showAddToCalendarButton ?
						<Button
							onClick={openAddToCalendarModal}
							classButton={classNames("secondary with-svg", secondaryButtonOverrides)}
							template={template}
							isDisabled={paidSessions && !sessionAccess}
						>
							<Icon name={ICONS.CALENDAR_ADD} color="" size={22} />
						</Button>
						: null
					}
				</div>
			</div>
		</div>
	);

	const renderCalendarListViewSessionCard = () => {

		const isMediumSizedDisplay = !isLessThan768 && isLessThan1024;

		const showDropdownMenu = isMediumSizedDisplay && (showAddToCalendarButton || (myListActive && userHasAccess));

		return (
			<div className={classNames("agenda-session-card", { 'featured-card': featuredCard, 'action-menu-open': actionMenuOpen })}>
				<OptionalComponent display={!disableSessionThumbnails}>
					<div className={classNames("agenda-session-card-image", moduleCustomHeadingOverrides)} onClick={handleSessionClick}>
						{session.image ? <img src={session.image} alt={"session image"} /> : <img src={defaultImage} alt={"session image"} />}
					</div>
				</OptionalComponent>
				<div className="agenda-session-card-content" onClick={handleSessionClick}>
					<div className="agenda-session-card-info evt-typography-caption-2 text-ellipsis">
						{timestampText}
					</div>
					<TypographyItem
						className={classNames('agenda-session-card-title evt-typography-h4 text-ellipsis', moduleTitleOverrides)}
						tagName="h3"
					>
						{t(`agenda.session.${session.session}.title`, session.title[language] as string || session.title.base || '')}
					</TypographyItem>
					<TypographyItem className={classNames('agenda-session-card-description evt-typography-subtitle-b3',
						{ 'agenda-session-card-description-hover-calendar': showAddToCalendarButton },
						{ 'agenda-session-card-description-hover-no-calendar': !showAddToCalendarButton },
						'quill-editor', moduleDescriptionOverrides,
						{ 'is-safari': isSafari })}
					>
						<RenderEditorDescription
							description={sessionCardDescription}
							useNew
						/>
					</TypographyItem>
					<SessionTags tags={sessionTags} />
				</div>
				<div className="agenda-session-card-buttons">
					{/* Mobile */}
					<OptionalComponent display={isLessThan768}>
						<>
							<Button
								classButton={classNames("primary", secondaryButtonOverrides)}
								template={template}
								onClick={handleSessionClick}
								isDisabled={paidSessions && !sessionAccess}
							>
								{buttonText}
							</Button>
							<OptionalComponent display={showAddToCalendarButton}>
								<Button
									onClick={openAddToCalendarModal}
									classButton={classNames("secondary", "with-svg", secondaryButtonOverrides)}
									template={template}
									isDisabled={paidSessions && !sessionAccess}
								>
									<Icon name={ICONS.CALENDAR_ADD} color="" size={22} />
								</Button>
							</OptionalComponent>
							<OptionalComponent display={myListActive && userHasAccess}>
								<Button
									classButton={classNames("secondary", "with-svg", secondaryButtonOverrides)}
									template={template}
									onClick={handleMyListClick}
									isDisabled={false}
									title={myListButtonText}
								>
									{sessionInMyList ?
										<Icon name={ICONS.DONE} color="" size={20} /> :
										<Icon name={ICONS.ADD} color="" size={20} />
									}
								</Button>
							</OptionalComponent>
						</>
					</OptionalComponent>

					{/* Tablet */}
					<OptionalComponent display={showDropdownMenu}> 
						<div className="action-menu">
							<NavigationDropdown
								title=""
								edge={isLessThan1024 ? 'right' : 'left'}
								className="menu-dropdown"
								buttonClassName="evt-button secondary with-svg"
								setModalOpen={setActionMenuOpen}
								renderCustomIcon={() => <EIcon name="more-horiz" size={22} />}
							>
								<>
									{myListActive && userHasAccess &&
										<button className="no-style no-padding" onClick={handleMyListClick}>
											{myListButtonText}
										</button>
									}
									{showAddToCalendarButton &&
										<button
											className="no-style no-padding"
											disabled={paidSessions && !sessionAccess}
											onClick={openAddToCalendarModal}
										>
											Add to calendar
										</button>
									}
									{isLessThan1024 && !isLessThan768 &&
										<button
											className="no-style no-padding"
											disabled={paidSessions && !sessionAccess}
											onClick={handleSessionClick}
										>
											{buttonText}
										</button>
									}
								</>
							</NavigationDropdown>
						</div>
					</OptionalComponent>

					{/* Desktop */}
					<OptionalComponent display={!isLessThan1024}>
						<>
							<OptionalComponent display={showAddToCalendarButton}>
								<Button
									onClick={openAddToCalendarModal}
									classButton={classNames("secondary", "with-svg", secondaryButtonOverrides)}
									template={template}
									isDisabled={paidSessions && !sessionAccess}
								>
									<Icon name={ICONS.CALENDAR_ADD} color="" size={22} />
								</Button>
							</OptionalComponent>
							<OptionalComponent display={myListActive && userHasAccess}>
								<Button
									classButton={classNames("secondary", "with-svg", secondaryButtonOverrides)}
									template={template}
									onClick={handleMyListClick}
									isDisabled={false}
									title={myListButtonText}
								>
									{sessionInMyList ?
										<Icon name={ICONS.DONE} color="" size={20} /> :
										<Icon name={ICONS.ADD} color="" size={20} />
									}
								</Button>
							</OptionalComponent>
							<Button
								classButton={classNames("primary", secondaryButtonOverrides)}
								template={template}
								onClick={handleSessionClick}
								isDisabled={paidSessions && !sessionAccess}
							>
								{buttonText}
							</Button>
						</>
					</OptionalComponent>

				</div>
			</div>
		);
	};

	return (
		<>
			{isCalendarLayout ? renderCalendarListViewSessionCard() : renderStandardSessionCard()}
			{event?.uuid ? (
				<Suspense fallback="">
					<AddToCalendarModal
						open={openAddToCalendar}
						close={closeAddToCalendar}
						session={session}
						eventName={event.name}
						language={language}
						isSingleSessionNoHomepage={event?.sessions?.length === 1 && !event?.homepage}
					/>
				</Suspense>
			) : null}
		</>
	);
};

export default SessionCard;
